import { createRouter, createWebHistory } from 'vue-router';
import axios from 'axios';
import { useUserStore } from '@/stores/user';

// Existing imports
import HomePage from '@/views/HomePage.vue';
import LoginPage from '@/views/LoginPage.vue';
import ForgotPassword from '@/views/ForgotPassword.vue';
import ResetPassword from '@/views/ResetPassword.vue';
import ProfilePage from '@/views/ProfilePage.vue';
import ProfileEdit from '@/views/ProfileEdit.vue';
import WalletPage from '@/views/WalletPage.vue';
import SupportPage from '@/views/SupportPage.vue';
import PrivacyPolicyPage from '@/views/PrivacyPolicyPage.vue';
import HandbookDashboard from '@/views/HandbookDashboard.vue';
import HandbookArticleCreate from '@/views/HandbookArticleCreate.vue';
import HandbookArticleEdit from '@/views/HandbookArticleEdit.vue';
import HandbookArticleModal from '@/views/HandbookArticleModal.vue';
import HandbookCategoryForm from '@/views/HandbookCategoryForm.vue';

// Forum imports
import ForumDashboard from '@/views/ForumDashboard.vue';
import ForumCategoryForm from '@/views/ForumCategoryForm.vue';
import ForumCategoryDelete from '@/views/ForumCategoryDelete.vue';
import ForumThreadCreate from '@/views/ForumThreadCreate.vue';
import ForumThreadDetail from '@/views/ForumThreadDetail.vue';
import ForumPostEdit from '@/views/ForumPostEdit.vue';
import ForumPostDelete from '@/views/ForumPostDelete.vue';

// Inbox import
import Inbox from '@/views/InboxPage.vue';

// SMS Blast import
import SmsBlast from '@/views/SmsBlast.vue';

// VIP customer import
import CustomerProfile from '@/views/CustomerProfile.vue';
import EditVIPProfile from '@/views/EditVIPProfile.vue';

const routes = [
  { path: '/', redirect: '/home' },
  { path: '/home', name: 'HomePage', component: HomePage },
  { path: '/login', name: 'LoginPage', component: LoginPage },
  { path: '/forgot-password', name: 'ForgotPassword', component: ForgotPassword },
  { path: '/reset/:uid/:token', name: 'ResetPassword', component: ResetPassword },
  { path: '/support', name: 'SupportPage', component: SupportPage },
  { path: '/privacy', name: 'PrivacyPolicyPage', component: PrivacyPolicyPage },
  { path: '/profile', name: 'ProfilePage', component: ProfilePage, meta: { requiresAuth: true }},
  { path: '/profile/edit', name: 'ProfileEdit', component: ProfileEdit, meta: { requiresAuth: true }},
  { path: '/wallet', name: 'WalletPage', component: WalletPage, meta: { requiresAuth: true }},

  // Inbox route
  { path: '/inbox', name: 'Inbox', component: Inbox, meta: { requiresAuth: true }},

  // SMS Blast route
  { path: '/sms-blast', name: 'SmsBlast', component: SmsBlast, meta: { requiresAuth: true }},

  // Handbook routes
  { path: '/handbook', name: 'HandbookDashboard', component: HandbookDashboard, meta: { requiresAuth: true }},
  {
    path: '/handbook/article/create',
    name: 'HandbookArticleCreate',
    component: HandbookArticleCreate,
    meta: { requiresAuth: true }
  },
  {
    path: '/handbook/article/:id/edit',
    name: 'HandbookArticleEdit',
    component: HandbookArticleEdit,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/handbook/article/:id',
    name: 'HandbookArticleModal',
    component: HandbookArticleModal,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/handbook/category/create',
    name: 'CategoryCreate',
    component: HandbookCategoryForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/handbook/category/:id/edit',
    name: 'CategoryEdit',
    component: HandbookCategoryForm,
    props: true,
    meta: { requiresAuth: true }
  },

  // Forum Routes
  { path: '/forum', name: 'ForumDashboard', component: ForumDashboard, meta: { requiresAuth: true } },
  {
    path: '/forum/category/create',
    name: 'ForumCategoryCreate',
    component: ForumCategoryForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/forum/category/:id/edit',
    name: 'ForumCategoryEdit',
    component: ForumCategoryForm,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/forum/category/:id/delete',
    name: 'ForumCategoryDelete',
    component: ForumCategoryDelete,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/forum/create',
    name: 'ForumThreadCreate',
    component: ForumThreadCreate,
    meta: { requiresAuth: true }
  },
  {
    path: '/forum/:threadId',
    name: 'ForumThreadDetail',
    component: ForumThreadDetail,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/forum/post/:postId/edit',
    name: 'ForumPostEdit',
    component: ForumPostEdit,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/forum/post/:postId/delete',
    name: 'ForumPostDelete',
    component: ForumPostDelete,
    props: true,
    meta: { requiresAuth: true }
  },

  // BI Dashboard (lazy-load)
  {
    path: '/bi-dashboard',
    name: 'BIDashboard',
    component: () => import('@/views/BIDashboard.vue'),
    meta: { requiresAuth: true }
  },

  // Announcement routes
  {
    path: '/announcement/create',
    name: 'AnnouncementCreate',
    component: () => import('@/views/AnnouncementCreate.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/announcement/edit/:id',
    name: 'AnnouncementEdit',
    component: () => import('@/views/AnnouncementEdit.vue'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/daily-tasks',
    name: 'DailyTasks',
    component: () => import('@/views/DailyTasks.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/daily-tasks/create',
    name: 'DailyTaskCreate',
    component: () => import('@/views/DailyTaskCreate.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/daily-tasks/:id/edit',
    name: 'DailyTaskEdit',
    component: () => import('@/views/DailyTaskEdit.vue'),
    props: true,
    meta: { requiresAuth: true }
  },

  // Stock routes
  {
    path: '/stock',
    name: 'StockManagement',
    component: () => import('@/views/StockManagement.vue'),
    meta: { requiresAuth: true }
  },

  // VIP routes
  { path: '/customer/profile', name: 'CustomerProfile', component: CustomerProfile, meta: { requiresAuth: true }},
  { path: '/customer/profile/edit', name: 'EditVIPProfile', component: EditVIPProfile, meta: { requiresAuth: true }},
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// 1) Guard: Handle the "switch_bar" query parameter.
router.beforeEach(async (to, from, next) => {
  if (to.query.switch_bar) {
    const newBar = to.query.switch_bar;
    try {
      await axios.post('/api/switch-bar/', { bar: newBar }, { withCredentials: true });
      const userStore = useUserStore();
      await userStore.fetchUser();
      const newQuery = { ...to.query };
      delete newQuery.switch_bar;
      delete newQuery.highlight_type;
      next({ ...to, query: newQuery });
    } catch (error) {
      console.error('Error switching bar:', error);
      next();
    }
  } else {
    next();
  }
});

// 2) Guard: If the route is "SmsBlast", check user role.
router.beforeEach(async (to, from, next) => {
  if (to.name === 'SmsBlast') {
    const userStore = useUserStore();
    if (!userStore.user) {
      await userStore.fetchUser();
    }
    const user = userStore.user;
    if (!user || (user.role !== 'manager' && user.role !== 'supermanager')) {
      return next({ name: 'HomePage' });
    }
  }
  next();
});

// 2b) Guard: If the route is "BIDashboard", check user role.
router.beforeEach(async (to, from, next) => {
  if (to.name === 'BIDashboard') {
    const userStore = useUserStore();
    if (!userStore.user) {
      await userStore.fetchUser();
    }
    const user = userStore.user;
    if (!user || (user.role !== 'manager' && user.role !== 'supermanager')) {
      return next({ name: 'HomePage' });
    }
  }
  next();
});

// 3) Guard: Force VIP users to only access their VIP profile pages.
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  if (!userStore.user) {
    await userStore.fetchUser();
  }
  const user = userStore.user;
  if (user && user.is_vip && to.meta.requiresAuth) {
    if (to.path !== '/customer/profile' && to.path !== '/customer/profile/edit' && to.path !== '/wallet') {
      return next('/customer/profile');
    }
  }
  next();
});

// 4) Guard: For onboarding users, allow only specific routes.
// Allowed routes: HomePage, ProfilePage, ProfileEdit, and all Handbook-related routes.
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  if (!userStore.user) {
    await userStore.fetchUser();
  }
  const user = userStore.user;
  if (user && user.role === 'onboarding' && to.meta.requiresAuth) {
    const allowedForOnboarding = [
      'HomePage',
      'ProfilePage',
      'ProfileEdit',
      'WalletPage',
      'HandbookDashboard',
      'HandbookArticleCreate',
      'HandbookArticleEdit',
      'HandbookArticleModal',
      'CategoryCreate',
      'CategoryEdit'
    ];
    if (!allowedForOnboarding.includes(to.name)) {
      return next({ name: 'HomePage' });
    }
  }
  next();
});

// 5) Guard: Block all requiresAuth routes if not logged in
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  if (!userStore.user) {
    await userStore.fetchUser();
  }

  if (to.meta.requiresAuth && !userStore.user) {
    return next({ name: 'HomePage' });
  }

  next();
});

export default router;
